"use client";

import * as React from "react";
import { FiAlertTriangle } from "react-icons/fi";
import { tv, type VariantProps } from "tailwind-variants";

const textarea = tv({
  slots: {
    parent: "flex flex-col font-roboto",
    label: "mb-2 text-body-1-400",
    baseInput: [
      "peer flex min-h-[96px] rounded-xl border bg-white px-4 py-2 font-roboto",
      "border-neutral-300",
      "hover:border-neutral-400 hover:ring-inset hover:ring-neutral-400 [&:not(:disabled)]:hover:ring-[0.6px]",
      "focus:border-neutral-600 focus:outline-none focus:ring-inset [&:not(:disabled)]:focus:border [&:not(:disabled)]:focus:ring-[0.6px] [&:not(:disabled)]:focus:ring-neutral-600",
    ],
    baseInputWrapper: "peer relative flex flex-col",
    alertIcon:
      "absolute right-5 top-5 hidden transform text-sentiment-negative-300",
    errorText: "hidden text-body-2-300 text-sentiment-negative-300",
    helperText: "text-body-2-300 text-neutral-600",
  },
  variants: {
    variant: {
      default: {
        baseInput: "",
      },
      border: {
        baseInput: "border",
      },
    },
    color: {
      white: {
        baseInput: "bg-white",
      },
      teal: {
        baseInput: "bg-secondary-100",
      },
    },
    value: {
      true: {
        baseInput: "valid:border-neutral-600",
        alertIcon: "hidden",
        errorText: "",
      },
      false: {
        baseInput: "",
        alertIcon: "",
        errorText: "",
      },
    },
    visited: {
      true: {
        baseInput: "invalid:border invalid:border-sentiment-negative-300",
        alertIcon: "peer-invalid:block",
        errorText: "peer-has-[:invalid]:block",
      },
      false: {
        baseInput: "",
        alertIcon: "",
        errorText: "",
      },
    },
    error: {
      true: {
        baseInput:
          "[&:not(:disabled)]:border [&:not(:disabled)]:border-sentiment-negative-300",
        alertIcon: "block",
        errorText: "block",
      },
      false: {
        baseInput: "",
        alertIcon: "",
        errorText: "",
      },
    },
    disabled: {
      true: {
        baseInput:
          "text-neutral-500 disabled:cursor-not-allowed disabled:border-b disabled:border-neutral-500 disabled:bg-neutral-100 disabled:outline-none",
        alertIcon: "hidden",
        errorText: "hidden",
        helperText: "hidden",
      },
      false: {
        baseInput: "",
        alertIcon: "",
        errorText: "",
        helperText: "",
      },
    },
  },
  compoundVariants: [
    {
      variant: "border",
      disabled: true,
      className: "border-b",
    },
  ],
  defaultVariants: {
    variant: "default",
    color: "white",
  },
});

type TextareaVariant = VariantProps<typeof textarea>;

export interface TextareaProps
  extends Omit<React.InputHTMLAttributes<HTMLTextAreaElement>, "color">,
    Omit<TextareaVariant, "value" | "visited"> {
  label?: string;
  helperText?: string;
  error?: boolean;
  errorMessage?: string;
  rows?: number;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      required,
      disabled,
      error,
      errorMessage = "Error",
      color,
      variant,
      label,
      helperText,
      rows = 6,
      onBlur,
      ...props
    },
    ref
  ) => {
    const [hasValue, setHasValue] = React.useState(!!props.value);
    const [visited, setVisited] = React.useState(false);
    const {
      parent,
      baseInput,
      baseInputWrapper,
      label: labelClass,
      errorText,
      helperText: helperTextClass,
      alertIcon,
    } = textarea({
      color,
      value: hasValue,
      visited,
      error,
      disabled,
      variant,
    });

    const labelText = label ? `${label}${required ? " *" : ""}` : "";
    const placeholder = props.placeholder || labelText;

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setHasValue(!!e.target.value);
      setVisited(true);
      if (onBlur) {
        onBlur(e);
      }
    };

    return (
      <div className={parent({ class: className })}>
        {label && (
          <label
            htmlFor={props.id}
            className={labelClass()}
          >
            {labelText}
          </label>
        )}
        <div className={baseInputWrapper()}>
          <textarea
            ref={ref}
            className={baseInput()}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            rows={rows}
            onBlur={handleBlur}
            {...props}
          />
          <FiAlertTriangle
            size={20}
            aria-label="alert-icon"
            className={alertIcon()}
          />
        </div>
        {helperText && (
          <span
            role="note"
            className={helperTextClass()}
          >
            {helperText}
          </span>
        )}
        <span
          role="alert"
          className={errorText()}
        >
          {errorMessage}
        </span>
      </div>
    );
  }
);
Textarea.displayName = "Textarea";

export default Textarea;
